import { twMerge } from 'tailwind-merge'
import { StateBlock } from '~/components/ui'
import { Phase } from '../utils/killrace'

/**
 * Kill Race chrome.
 *
 * The notched-panel motif this page introduced is now shared — it lives in `~/components/ui/Panel`
 * and is used by the dashboard and account pages too. Everything generic is re-exported from here
 * so the Kill Race call sites keep their existing imports; only the genuinely race-specific bits
 * are defined below.
 */
export {
    ACCENT,
    Chip,
    ErrorBlock,
    GoArrow,
    notch,
    OutlineButton,
    Panel,
    PRIZE_GREEN,
    SectionHeading,
    Skeleton,
    Stat,
    StateBlock,
} from '~/components/ui'

const skull = new URL('../assets/skull.png', import.meta.url).href

/** Kill Race keeps its own skull art; the shared `EmptyBlock` uses the site-wide one. */
export const EmptyBlock = (props: Omit<Parameters<typeof StateBlock>[0], 'icon' | 'image'>) => (
    <StateBlock image={skull} {...props} />
)

const PHASE_TONE: Record<Phase, { label: string; color: string; className: string }> = {
    Live: { label: 'Live', color: '#00FF90', className: 'border-[#00FF90]/40 bg-[#00FF90]/10 text-[#00FF90]' },
    Upcoming: { label: 'Upcoming', color: '#03CEFF', className: 'border-[#03CEFF]/40 bg-[#03CEFF]/10 text-[#03CEFF]' },
    Completed: { label: 'Finished', color: '#8A8A8A', className: 'border-white/20 bg-white/5 text-white/50' },
    Disabled: { label: 'Disabled', color: '#8A8A8A', className: 'border-white/20 bg-white/5 text-white/40' },
}

/**
 * The colour and wording a phase is allowed to use. Exported because the list view paints the
 * same phase as a rim down the left edge of a row, and two places inventing their own green is
 * how a palette drifts.
 */
export const phaseTone = (phase: Phase) => PHASE_TONE[phase] ?? PHASE_TONE.Completed

export const PhaseBadge = ({ phase, className }: { phase: Phase; className?: string }) => {
    const tone = phaseTone(phase)
    return (
        <span
            className={twMerge(
                'inline-flex items-center gap-2 border px-2.5 py-1 text-2xs font-black uppercase tracking-[0.2em] backdrop-blur-sm',
                tone.className,
                className
            )}
        >
            <span className='relative flex h-1.5 w-1.5'>
                {phase === 'Live' && (
                    <span
                        className='absolute inline-flex h-full w-full animate-ping rounded-full opacity-75'
                        style={{ backgroundColor: tone.color }}
                    />
                )}
                <span
                    className='relative inline-flex h-1.5 w-1.5 rounded-full'
                    style={{ backgroundColor: tone.color }}
                />
            </span>
            {tone.label}
        </span>
    )
}
